import { PaginatedData, PaginatedResponse } from "../../types";
import { fixDoubleEscapedStringResponse } from "../../utils/fixDoubleEscapedStringResponse";
import { APIQuery } from "../../utils/sharedTypes";
import { ApiServiceCollection } from "../ApiServiceCollection.class";
import { ApiResponse } from "../RequestManager.class";
import { ApiServiceEndpoint } from "../api.types";
import { BundleListAPI } from "../mocks/database/tables/bundles.mockTable";
import { BundleOrganizationAttributionAPI } from "../mocks/database/tables/bundles_organizations_attributions.mockTable";
import { Bundle, BundleApi, BundleAttribution } from "./types";

export class BundleService extends ApiServiceCollection {
    getBundles: ApiServiceEndpoint<PaginatedData<Bundle>> = ({
        organizationGuid, skip = 0, limit = 10, query }: { skip: number; limit: number; organizationGuid: string, query?: APIQuery }
    ) => {
        const queryParams = this.endpointParamsToQueryParams({ skip, limit, query });
        const filterQueryParams = new URLSearchParams(queryParams);
        filterQueryParams.append('organization_guid', organizationGuid);

        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL as string}/organization/${organizationGuid}/bundle?${queryParams}`, {
            method: 'GET',
            headers: {
                ...this.getJSONHeaders()
            },
        }, {
            mock: this.mocked,
            mockData: this.getMockData({
                tableName: 'bundles',
                queryParams: filterQueryParams
            }),
            getMockStatus: () => this.getNextMockStatus('BundleService.getBundles'),
            transformResponsePayload: async (apiResponse: ApiResponse) => {
                const response = apiResponse.response;
                const respData = await response?.json() as PaginatedResponse<BundleListAPI>;
                return {
                    response: response as Response,
                    success: true,
                    jsonBody: {
                        total: respData.total,
                        data: (respData.bundles || respData.data || []).map(({
                            bundle_guid, display_name
                        }) => ({
                            bundleGuid: bundle_guid,
                            displayName: display_name,
                            organizationGuid
                        } as Bundle))
                    }
                } as ApiResponse<PaginatedData>
            }
        })
            .catch(error => error);
    };

    getBundleById: ApiServiceEndpoint<Bundle> = (bundleGuid: string) => {
        return this.standardAuthRequest<Bundle>(`${process.env.REACT_APP_MANAGEMENT_API_URL}/bundle/${bundleGuid}`, {
            method: 'GET',
            headers: {
                ...this.getJSONHeaders()
            },
        }, {
            mock: this.mocked,
            mockData: this.getMockDataSingular({
                tableName: 'bundles',
                queryParams: { bundle_guid: bundleGuid }
            }),
            getMockStatus: () => this.getNextMockStatus('BundleService.getBundleById'),
            transformResponsePayload: async (apiResponse: ApiResponse) => {
                const response = apiResponse.response;
                const respData = await response?.json() as BundleApi;
                return ({
                    success: true,
                    response,
                    jsonBody: {
                        bundleGuid: respData.bundle_guid,
                        organizationGuid: respData.organization_guid,
                        displayName: respData.display_name,
                        parentGuid: respData.parent_guid,
                        children: (respData.children || []).map(child => ({
                            bundleGuid: child.bundle_guid,
                            displayName: child.display_name,
                            type: child.type
                        }))
                    } as Bundle
                })
            }
        })
            .catch(error => error);
    }

    createBundle: ApiServiceEndpoint<string> = (data: Omit<Bundle, "bundleGuid">) => {
        let response: Response | null = null;
        const reqData: Record<string, any> = {
            "display_name": data.displayName,
            "organization_guid": data.organizationGuid,
            "type": data.type,
            "parent_guid": data.parentGuid
        };
        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL as string}/bundle`, {
            method: 'POST',
            headers: {
                ...this.getJSONHeaders()
            },
            body: JSON.stringify({
                "display_name": data.displayName,
                "organization_guid": data.organizationGuid,
                "type": data.type,
                "parent_guid": data.parentGuid
            })
        }, {
            mock: this.mocked,
            mockData: () => {
                try {
                    const existingRec = this.mockCRUD!.db().getTableByName('bundles').query([{
                        fieldName: 'display_name',
                        value: data.displayName
                    }]);
                    if (existingRec.length) {
                        return {
                            data: null,
                            status: 409,
                            error: {
                                message: 'A bundle with this name already exists',
                                code: 'CONFLICT'
                            }
                        }
                    }
                    const bundleRec = this.mockCRUD!.db().getTableByName('bundles').createRecord(reqData);
                    return {
                        data: bundleRec.values['bundle_guid'],
                        status: 200
                    }
                } catch (e) {
                    return {
                        data: { error: `An unexpected error occurred` },
                        status: 500,
                        error: {
                            message: `An unexpected error occurred`,
                            code: 'INTERNAL_SERVER'
                        }
                    }
                }
            },
            getMockStatus: () => this.getNextMockStatus('BundleService.createBundle')
        })
            .then(resp => {
                response = resp.response!;
                return resp.response!.text()
            })
            .then(respData => {
                return {
                    success: true,
                    response: response as Response,
                    textBody: fixDoubleEscapedStringResponse(respData)
                }
            })
            .catch(error => {
                if (error.error?.status === 409) {
                    return {
                        ...error,
                        error: new Error("A Bundle with this name already exists.", {
                            cause: { code: "AUTH_FAILED", status: 403 }
                        })
                    }
                }
                return error;
            })
    }

    updateBundle: ApiServiceEndpoint<void> = ({ parentGuid, bundleGuid }: { parentGuid: string; bundleGuid: string; }) => {
        const updateBundleData = {} as Record<string, any>;
        updateBundleData['parent_guid'] = parentGuid ?? null;

        let rawResp: Response | null = null;
        return this.standardAuthRequest(
            `${process.env.REACT_APP_MANAGEMENT_API_URL as string}/bundle/${bundleGuid}`,
            {
                method: 'PATCH',
                headers: {
                    ...this.getJSONHeaders()
                },
                body: JSON.stringify(updateBundleData)
            }, {
            mock: this.mocked,
            mockData: () => {
                try {
                    this.mockCRUD!.db().getTableByName('bundles').updateRecords([
                        {
                            fieldName: 'bundle_guid',
                            value: bundleGuid
                        }
                    ], updateBundleData);
                    return {
                        data: null,
                        status: 204
                    }
                } catch (e) {
                    return {
                        data: { error: `An unexpected error occurred` },
                        status: 500,
                        error: {
                            message: `An unexpected error occurred`,
                            code: 'INTERNAL_SERVER'
                        }
                    }
                }
            },
            getMockStatus: () => this.getNextMockStatus('BundleService.updateBundle')
        })
            .then(() => ({
                success: true,
                response: rawResp
            }))
            .catch(error => error);
    };


    getAttributions: ApiServiceEndpoint<BundleAttribution[]> = (bundleGuid: string) => {
        // TODO - BE - This response appears to not be paginated from the server
        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL}/bundle/${bundleGuid}/attribution`, {
            method: 'GET',
            headers: {
                ...this.getJSONHeaders()
            },
        }, {
            mock: this.mocked,
            mockData: this.getMockData({
                tableName: 'bundles_organizations_attributions',
                queryParams: { bundle_guid: bundleGuid, deleted: 0 }
            }),
            getMockStatus: () => this.getNextMockStatus('BundleService.getAttributions'),
            transformResponsePayload: async (apiResponse: ApiResponse) => {
                const response = apiResponse.response;
                const respData = await response?.json() as BundleOrganizationAttributionAPI[];
                return {
                    success: true,
                    response: response,
                    jsonBody: respData.map(bundleAttr => ({
                        attributionGuid: bundleAttr.attribution_guid,
                        displayName: bundleAttr.display_name,
                        status: bundleAttr.status,
                        type: bundleAttr.type,
                        organizationAttributionGuid: bundleAttr.organization_attribution_guid
                    } as BundleAttribution))
                }
            }
        })
        .catch(error => error);
    }

    addBundleAttribution: ApiServiceEndpoint<string> = ({ bundleGuid, organizationAttributionGuid }: {
        bundleGuid: string, organizationAttributionGuid: string
    }) => {
        let rawResp: Response | null = null;
        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL}/bundle/${bundleGuid}/attribution`, {
            method: 'POST',
            headers: {
                ...this.getJSONHeaders()
            },
            body: JSON.stringify({
                "organization_attribution_guid": organizationAttributionGuid
            })
        }, {
            mock: this.mocked,
            mockData: () => {
                try {
                    const bundle_id = this.mockCRUD!.resolveGuidToId('bundles', bundleGuid);
                    const organization_attribution_id = this.mockCRUD!.resolveGuidToId('organizations_attributions', organizationAttributionGuid);
                    this.mockCRUD!.db().getTableByName('bundles_organizations_attributions').createRecord({
                        bundle_id,
                        organization_attribution_id
                    });
                    return {
                        data: null,
                        status: 204
                    }
                } catch (e) {
                    return {
                        data: { error: `An unexpected error occurred` },
                        status: 500,
                        error: {
                            message: `An unexpected error occurred`,
                            code: 'INTERNAL_SERVER'
                        }
                    }
                }
            },
            getMockStatus: () => this.getNextMockStatus('BundleService.addBundleAttribution')
        })
            .then(resp => {
                rawResp = resp.response!;
                return resp.response!.text();
            })
            .then(respData => {
                return {
                    success: true,
                    response: rawResp,
                    jsonBody: respData // bundle_attribution_guid
                }
            })
            .catch(error => error);
    }

    deleteBundleAttribution = ({ bundleGuid, organizationAttributionGuid }: { bundleGuid: string, organizationAttributionGuid: string }
    ) => {
        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL}/bundle/${bundleGuid}/attribution/${organizationAttributionGuid}`, {
            method: 'DELETE',
            headers: {
                ...this.getJSONHeaders()
            },
        }, {
            mock: this.mocked,
            mockData: () => {
                try {
                    const bundle_id = this.mockCRUD!.resolveGuidToId('bundles', bundleGuid);
                    const organization_attribution_id = this.mockCRUD!.resolveGuidToId('organizations_attributions', organizationAttributionGuid);
                    this.mockCRUD!.db().getTableByName('bundles_organizations_attributions').updateRecords([
                        {
                            fieldName: 'bundle_id',
                            value: bundle_id
                        },
                        {
                            fieldName: 'organization_attribution_id',
                            value: organization_attribution_id
                        }
                    ], { deleted: 1 });
                    return {
                        data: null,
                        status: 204
                    }
                } catch (e) {
                    return {
                        data: { error: `An unexpected error occurred` },
                        status: 500,
                        error: {
                            message: `An unexpected error occurred`,
                            code: 'INTERNAL_SERVER'
                        }
                    }
                }
            },
            getMockStatus: () => this.getNextMockStatus('BundleService.deleteBundleAttribution')
        })
            .then(response => {
                return {
                    response,
                    jsonBody: null
                }
            })
            .catch(error => error);
    }
}